<template>
  <div :class="$style.container">
    <div :class="$style.headerContainer">
      <h2 :class="$style.header">
        Submit a Support Ticket
      </h2>
      <p>
        For commonly asked questions, check out our helpful
        <a
          :href="KbArticleUrls.support"
          rel="noopener noferrer"
          target="_blank"
        >
          support docs
        </a>
      </p>
    </div>
    <div
      v-if="displayForm"
      :class="$style.contentContainer"
    >
      <div :class="$style.formContainer">
        <InputOptionalValidation
          v-model="subject"
          v-validate="'required|max:255'"
          :class="$style.inputField"
          label="* Subject"
          :name="'subject'"
          :placeholder="subjectPlaceholder"
          :value="subject"
          @blur="handleSubjectBlur"
        />
        <TextareaOptionalValidation
          v-model="description"
          v-validate="'required|max:31950'"
          :class="$style.inputField"
          label="* How can we help?"
          :name="'description'"
          :placeholder="descriptionPlaceholder"
          :value="description"
        />
        <DropDown
          v-model="time"
          v-validate="'required'"
          :class="$style.timeDropDown"
          label="* Best time of day for reply?"
          name="time"
          :options="timesMt"
          :value="time"
        />
        <div
          v-if="!user.firstname || !user.lastname"
          :class="$style.nameContainer"
        >
          <InputOptionalValidation
            v-model="firstName"
            v-validate="'required|max:40'"
            :class="[$style.inputField, $style.nameField]"
            data-vv-as="first name"
            label="* First Name"
            :name="'firstname'"
            :value="firstName"
          />
          <InputOptionalValidation
            v-model="lastName"
            v-validate="'required|max:80'"
            :class="[$style.inputField, $style.nameField]"
            data-vv-as="last name"
            label="* Last Name"
            :name="'lastname'"
            :value="lastName"
          />
        </div>
        <ul :class="$style.attachmentList">
          <li
            v-for="(attachment, index) in attachments"
            :key="index"
            :class="$style.attachmentPill"
          >
            <p :class="$style.attachmentName">
              {{ attachment.name }}
            </p>
            <button
              :class="$style.deleteAttachmentButton"
              @click="handleDeleteClick(attachment, index)"
            >
              <CloseIcon />
            </button>
          </li>
        </ul>
        <div
          :class="$style.attachmentContainer"
        >
          <label
            :class="$style.dropFileHereLabel"
          >
            Add file(s) here (5mb limit)
          </label>
          <input
            id="file"
            accept="text/plain,.log,image/*"
            :class="$style.attachment"
            name="file"
            type="file"
            @change="handleFileUpload"
          >
          <label
            :class="$style.attachmentLabel"
            for="file"
          >
            choose a file
          </label>
          <p
            :class="$style.uploadError"
            :style="{visibility: isUploadError ? 'visible' : 'hidden'}"
          >
            File size is too large. You may attach larger files via email.
          </p>
        </div>
        <div
          :class="$style.actionContainer"
        >
          <p
            :class="$style.errorText"
            :style="{visibility: errorMessage ? 'visible' : 'hidden'}"
          >
            <MarkdownInline :mdTemplate="errorMessage" />
          </p>
          <div
            :class="$style.actionButtons"
          >
            <a
              :class="$style.actionCancel"
              @click="handleRedirect"
            >cancel</a>
            <FlatActionButton
              :action="handleSubmit"
              :buttonClass="['btnDefault', 'btnCTA']"
              :disabled="!enableSubmitFlag"
              :loading="loading"
              text="submit ticket"
            />
          </div>
        </div>
      </div>
      <ContactSupportKbArticles
        :class="$style.kbArticlesContainer"
        :initTrigger="handleInitTrigger"
        :subject="subject"
      />
    </div>
    <div
      v-if="!displayForm"
      :class="$style.thankYouContainer"
    >
      <FlyingPaperAirplane :class="$style.thankYouIcon" />
      <h2 :class="$style.thankYouHeader">
        Thank you for reaching out.
      </h2>
      <p
        v-if="caseNumber"
        :class="$style.thankYouDescription"
      >
        You will receive an email reply shortly. To follow this ticket <br>please reference ticket
        <span :class="$style.caseNumber">
          #{{ caseNumber }}
        </span> at
        <a
          :class="$style.supportLink"
          :href="KbArticleUrls.support"
          target="_blank"
        >
          support.jumpcloud.com
        </a>
      </p>
      <p
        v-else
        :class="$style.thankYouDescription"
      >
        You will receive an email confirmation shortly.
      </p>
      <FlatActionButton
        :action="handleRedirect"
        :buttonClass="['btnDefault', 'btnCTA']"
        :class="$style.okButton"
        text="ok"
      />
    </div>
  </div>
</template>

<script>
import { MarkdownInline } from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import CasesApi from 'api/v2/CasesApi';
import CloseIcon from 'public/img/icons/close.svg?inline';
import ContactSupportKbArticles from 'client/Support/ContactSupportKbArticles.vue';
import DropDown from 'client/components/FormValidation/DropDown.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import FlyingPaperAirplane from 'public/img/icons/flying-paper-airplane.svg?inline';
import InputOptionalValidation from 'client/components/FormValidation/Input.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import TextareaOptionalValidation from 'client/components/FormValidation/Textarea.vue';

const maxAttachmentSize = 5242880;

export default {
  name: 'ContactSupportMainPanel',

  components: {
    CloseIcon,
    ContactSupportKbArticles,
    DropDown,
    FlatActionButton,
    FlyingPaperAirplane,
    InputOptionalValidation,
    MarkdownInline,
    TextareaOptionalValidation,
  },

  data() {
    return {
      KbArticleUrls,
      attachments: [],
      attachmentsSize: 0,
      caseNumber: '',
      description: '',
      descriptionPlaceholder: 'Explain the issue or the problem you need help with, as well as the steps you\'ve taken.',
      displayForm: true,
      enableSubmitFlag: false,
      errorMessage: '',
      firstName: '',
      isServerError: false,
      isUploadError: false,
      lastName: '',
      loading: false,
      time: '',
      timesMt: [
        'Anytime',
        '3pm-11pm GMT/ 9am-5pm MT',
        '11pm-7am GMT/ 5pm-1am MT',
        '7am-3pm GMT/ 1am-9am MT',
      ],
      subject: '',
      subjectBlur: () => {},
      subjectPlaceholder: 'ex: How to set up Radius',
    };
  },

  computed: {
    ...mapGetters('SettingsModule', {
      user: 'getUser',
    }),
  },

  watch: {
    async description() {
      await this.$validator.validate('description');
      this.isMainActionEnabled();
    },

    async firstName() {
      await this.$validator.validate('firstname');
      this.isMainActionEnabled();
    },

    async lastName() {
      await this.$validator.validate('lastname');
      this.isMainActionEnabled();
    },

    async subject() {
      await this.$validator.validate('subject');
      this.isMainActionEnabled();
    },

    async time() {
      await this.$validator.validate('time');
      this.isMainActionEnabled();
    },
  },

  methods: {
    ...mapActions('SettingsModule', [
      'fetchUserSettings',
    ]),

    clearData() {
      this.subject = '';
      this.description = '';
      this.firstName = '';
      this.lastName = '';
      this.time = '';
      this.attachments = [];
    },

    handleDeleteClick(attachment, index) {
      this.attachments.splice(index, 1);
      this.attachmentsSize -= attachment.size;
      this.isUploadError = this.attachmentsSize > maxAttachmentSize;
      this.isMainActionEnabled();
    },

    handleFileUpload(event) {
      const [file] = event.currentTarget.files;
      if (file) {
        this.attachments.push(file);
        this.attachmentsSize += file.size;
        this.isUploadError = this.attachmentsSize > maxAttachmentSize;
      }
      // eslint-disable-next-line
      event.currentTarget.value = '';
      this.isMainActionEnabled();
    },

    handleInitTrigger(triggerFunc) {
      this.subjectBlur = triggerFunc;
    },

    handleRedirect() {
      this.clearData();
      this.$router.push({ name: 'Support' });
    },

    handleSubjectBlur() {
      this.subjectBlur();
    },

    handleSubmit() {
      this.loading = true;
      CasesApi.postContactSupport({
        description: this.description,
        subject: this.subject,
        replyTime: this.time,
        attachment: this.attachments,
        firstname: this.firstName,
        lastname: this.lastName,
      })
        .then((resp) => {
          const { caseNumber } = resp.data;

          this.caseNumber = caseNumber;
          this.displayForm = false;
          this.fetchUserSettings();

          this.$router.push({
            name: 'CasePortal',
            params: {
              displaySuccessNotificationForCaseNumber: caseNumber,
            },
          });
        })
        .catch((err) => {
          const defaultMessage = 'We were unable to submit your ticket. Please contact support directly with your issue at [support@jumpcloud.com.](mailto:support@jumpcloud.com)';
          this.errorMessage = get(err, 'response.data.message', defaultMessage);

          const status = get(err, 'response.status', 500);
          if (status >= 500) {
            this.errorMessage = defaultMessage;
            this.isServerError = true;
          }
        })
        .finally(() => {
          this.loading = false;
          this.isMainActionEnabled();
        });
    },

    hasModelChanged() {
      if (this.displayForm
        && (this.description
        || this.subject
        || this.time
        || (!this.user.firstname && this.firstName)
        || (!this.user.lastname && this.lastName)
        || this.attachments.length)) {
        return true;
      }
      return false;
    },

    isMainActionEnabled() {
      this.enableSubmitFlag = this.v2Errors.any() === false
      && this.isServerError === false
      && this.isUploadError === false
      && (this.v2Fields.subject && this.v2Fields.subject.valid === true)
      && this.v2Fields.description.valid === true
      && this.v2Fields.time.valid === true
      && (this.v2Fields.firstname ? this.v2Fields.firstname.valid === true : true)
      && (this.v2Fields.lastname ? this.v2Fields.lastname.valid === true : true);
    },
  },
};
</script>

<style module>
@value (
  chooseFileBlue,
  gradientDark2,
  gradientDark3,
  gradientLight4,
  jcRed,
  tealLight1
) from '~css/colors.css';
@value (max-width-large) from '~css/breakpoints.css';
@value (allTransition) from '~css/transitions.css';

.actionButtons {
  margin-left: auto;
}

.actionCancel {
  cursor: pointer;
  padding-right: 14px;
}

.actionContainer {
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  width: 100%;
}

.attachment {
  display: none !important;
}

.attachmentContainer {
  background-color: white;
  border: gradientLight4 2px;
  border-radius: 6px;
  border-style: dotted;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.attachmentLabel {
  color: chooseFileBlue;
  display: block;
  margin-top: 10px;
  text-align: center;
}

.attachmentList {
  list-style-type: none;
  padding-left: 0;
  width: 300px;
}

.attachmentPill {
  background-color: gradientLight4;
  border: none;
  border-radius: 8px;
  margin-top: 4px;
  padding: 6px 0 0 20px;
  text-decoration: none;
}

.attachmentPill .attachmentName {
  display: inline-block;
  margin-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 75%;
}

.caseNumber {
  font-weight: bold;
}

.container {
  padding: 30px 50px 50px 50px;
}

.contentContainer {
  display: flex;
  margin-top: 50px;
}

.deleteAttachmentButton {
  background-color: transparent;
  width: 23%;
}

.dropFileHereLabel {
  display: block;
  margin-top: 20px;
  text-align: center;
}

.errorText {
  color: jcRed;
  margin: 0 50px 10px;
  text-align: center;
}

.formContainer {
  max-width: 600px;
  min-width: 400px;
  width: 600px;
}

.header {
  font-weight: 700;
  margin-top: 0;
}

.headerContainer {
  justify-content: space-between;
}

.headerContainer .header {
  font-size: 2em;
  font-weight: 700;
  margin-top: 0;
}

.innerTopLeftContentContainer {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.inputField.inputField input,
.inputField.inputField textarea {
  background-color: white;
}

.inputField.inputField textarea:focus {
  background-color: tealLight1;
}

.kbArticlesContainer.kbArticlesContainer {
  flex-grow: 1;
  max-width: 550px;
  padding-left: 100px;
  padding-right: 0;
}

.nameContainer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.nameField {
  width: 49%;
}

.okButton.okButton {
  margin-top: 39px;
}

.thankYouContainer {
  margin: 70px auto;
  text-align: center;
  width: 100%;
}

.thankYouContainer.thankYouContainer h2 {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.thankYouContainer.thankYouContainer p {
  font-size: 1.2rem;
  line-height: 2rem;
}

.thankYouIcon {
  display: block;
  height: 172px;
  margin-left: auto;
  margin-right: auto;
  width: 241px;
}

.timeDropDown {
  width: 300px;
}

.uploadError {
  color: jcRed;
  text-align: center;
}

@media (max-width: max-width-large) {
  .contentContainer {
    align-items: center;
    flex-wrap: wrap;
  }

  .kbArticlesContainer.kbArticlesContainer {
    max-width: 600px;
    padding: 60px;
  }
}

</style>
